import React, { useEffect } from 'react'
import { Grid, GridColumn, GridRow } from 'semantic-ui-react'
import ControlledFormField from '../common/ControlledFormField'
import useLocalForm from '../../hooks/useLocalForm'
import { Button } from 'react-dxd-components'
import { isValid } from '../../state/slice/form'
import PinHelpModal from './PinHelpModal'
import { useDispatch } from 'react-redux'
import Message from '../common/Message'
import Loader from '../common/Loader'
import { useNavigate } from 'react-router-dom'
import { fetchMemberLite } from '../../state/reducers/fetchMemberLite'
import { validatePin } from '../../state/reducers/validatePin'
import useLoading from '../../hooks/useLoading'
import useScrollToTop from '../../hooks/useScrollToTop'
import _ from 'lodash'
import { requiredField } from '../../state/common/FORM_SEGMENTS'

const initialPinVerificationState = {
  error: '',
  pin: {
    ..._.pick(requiredField, ['value', 'dirty', 'valid', 'pattern', 'required']),
    error: '',
    pattern: '^\\d{7}$'
  },
  lastName: {
    ..._.pick(requiredField, ['value', 'dirty', 'valid', 'pattern', 'required']),
    error: ''
  }
}

export default function PinVerification () {
  const dispatch = useDispatch()
  const {
    state: pinValidationFormState,
    dispatch: setPinValidationFormState
  } = useLocalForm(initialPinVerificationState)
  const { isLoading, loading, done } = useLoading()
  const navigate = useNavigate()

  const onChange = (e) => {
    setPinValidationFormState({ key: `${e.target.id}.value`, value: e.target.value })
  }

  const onBlur = (e) => {
    setPinValidationFormState({ key: `${e.target.id}.dirty`, value: true })
  }

  useEffect(() => {
    setPinValidationFormState({ key: 'pin.valid', value: isValid(pinValidationFormState.pin) })
    // eslint-disable-next-line
  }, [pinValidationFormState.pin.value, pinValidationFormState.pin.dirty])

  useEffect(() => {
    setPinValidationFormState({ key: 'lastName.valid', value: isValid(pinValidationFormState.lastName) })
    // eslint-disable-next-line
  }, [pinValidationFormState.lastName.value, pinValidationFormState.lastName.dirty])

  const onClick = () => {
    loading()
    dispatch(validatePin({
      pin: pinValidationFormState.pin.value,
      lastName: pinValidationFormState.lastName.value
    }))
      .then(action => {
        // Error responses from the API come in the form of 200 status
        if (action.payload.errorMessage) {
          done()
          setPinValidationFormState({ key: 'error', value: action.payload.errorMessage })
        } else {
          dispatch(fetchMemberLite({
            compassMemberId: action.payload.compassMemberId,
            compassId: action.payload.compassId,
            pin: action.payload.pin
          }))
            .then(() => {
              done()
              navigate('/home')
            })
            .catch(() => {
              done()
              setPinValidationFormState({ key: 'error', value: 'Error fetching member data for PIN.' })
            })
        }
      })
      .catch(() => {
        done()
        setPinValidationFormState({ key: 'error', value: 'Error encountered validating PIN. Please try again.' })
      })
  }

  return useScrollToTop(<Grid container className='container-md'>
    <GridRow>
      <GridColumn width={16}>
        <h3>PIN Verification</h3>
        <p>Please enter the PIN from the email you received from Mutual of Omaha.</p>
      </GridColumn>
    </GridRow>
    <GridRow className={`horizontally-divided${isLoading ? ' Dimmed' : ''}`}>
      {isLoading && <Loader />}
      <GridColumn computer={8} tablet={8} mobile={16} className='u-mT5'>
        <ControlledFormField
          id='pin'
          label='PIN'
          error={!pinValidationFormState.pin.valid ? '7-digit PIN number required' : ''}
          detail={<PinHelpModal id='pin-help' trigger={<small className='u-link'>Pin not working?</small>} />}
          value={String(pinValidationFormState.pin.value)}
          onChange={onChange}
          onBlur={onBlur}
          maxLength={7}
        />
      </GridColumn>
      <GridColumn computer={8} tablet={8} mobile={16}>
        <ControlledFormField
          id='lastName'
          label='Last Name'
          error={!pinValidationFormState.lastName.valid && pinValidationFormState.lastName.value.length === 0 ? 'Required' : ''}
          value={pinValidationFormState.lastName.value}
          onChange={onChange}
          onBlur={onBlur}
        />
      </GridColumn>
    </GridRow>
    {pinValidationFormState.error && <GridRow>
      <GridColumn width={16}>
        <Message data-testid='pin-validation-error-message' type='critical' content={pinValidationFormState.error} />
      </GridColumn>
    </GridRow>}
    <GridRow>
      <GridColumn textAlign='right' floated='right' computer={3} tablet={4} mobile={16}>
        <Button
          disabled={
            !pinValidationFormState.lastName.value ||
            !pinValidationFormState.pin.value
          }
          onClick={onClick}
        >
          Submit
        </Button>
      </GridColumn>
    </GridRow>
  </Grid>)
}